import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field } from 'formik';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '@lightspeed/cirrus-modal';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Group from '@lightspeed/cirrus-group';
import Select from '@lightspeed/cirrus-select';
import Text from '@lightspeed/cirrus-text';

import { TERMINAL_TYPE } from 'shared_modules/Terminals';
import { GatewayType } from 'pages/store/selectors/gatewayConfig';

import Fields from './Fields';
import { generateValidationSchema, getTerminalTypeByGatewayID } from './utils';
import CayanActions from './CayanActions';

export const ERROR_CODES = {
  UNABLE_TO_PAIR_TERMINAL: 'unable_to_pair_terminal',
  VALIDATION_ERROR: 'validation_error',
};

export const ERROR_MESSAGE = {
  UNABLE_TO_PAIR_TERMINAL: 'Activation Code is invalid.',
  VALIDATION_ERROR: 'The name may not be greater than 50 characters.',
};

const renderError = (error, name = '') =>
  error && (
    <div className="cr-mb-2">
      <Alert type="warning" title={`Connection to '${name}' failed`} noCloseBtn>
        {error}
      </Alert>
    </div>
  );

class TerminalModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      testingError: null,
    };

    this.handleClose = this.handleClose.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.handleTestConnectionError = this.handleTestConnectionError.bind(this);
  }

  handleSave(values = {}) {
    const { terminalGatewayID } = this.props;

    this.props.onSuccess({
      type: values.type || getTerminalTypeByGatewayID(terminalGatewayID),
      ...values,
    });
  }

  handleClose(onCancel, handleReset) {
    return () => {
      onCancel();
      handleReset();
      this.setState({ testingError: null });
    };
  }

  handleTestConnectionError(error) {
    this.setState({
      testingError: error || null,
    });
  }

  render() {
    const {
      isOpen,
      isProcessing,
      onCancel,
      terminalParameters,
      terminalGatewayID,
      ccTerminalErrors,
      initialValues,
      edit,
    } = this.props;
    const { testingError } = this.state;

    const validationSchema = generateValidationSchema(terminalParameters);

    return (
      <Formik
        enableReinitialize
        onSubmit={this.handleSave}
        initialValues={initialValues}
        validationSchema={validationSchema}
        render={({
          errors,
          handleBlur,
          handleChange,
          isValid,
          handleSubmit,
          touched,
          values,
          setFieldError,
          handleReset,
        }) => (
          <Modal
            isOpen={isOpen}
            onRequestClose={this.handleClose(onCancel, handleReset)}
            shouldCloseOnOverlayClick={!isProcessing}
            closeOnEsc={!isProcessing}
          >
            <ModalHeader showCloseButton={false}>
              {edit ? 'Edit a Payment Terminal' : 'Add a Payment Terminal'}
            </ModalHeader>
            <ModalBody>
              <div className="cirrus" style={{ width: '400px' }}>
                {renderError(testingError, values.name)}
                {terminalGatewayID === GatewayType.TYRO && (
                  <div className="cr-mb-2">
                    <Text bodySmall bold>
                      Terminal Type
                    </Text>
                    <Field
                      name="type"
                      render={({ field }) => (
                        <Select {...field}>
                          <option value={TERMINAL_TYPE.YOMANI}>Yomani</option>
                          <option value={TERMINAL_TYPE.YOXIMO}>Yoximo</option>
                        </Select>
                      )}
                    />
                  </div>
                )}
                <Fields
                  parameters={terminalParameters}
                  errors={errors}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  touched={touched}
                  values={values}
                  ccTerminalErrors={ccTerminalErrors}
                  setFieldError={setFieldError}
                />
                <CayanActions
                  onTestingError={this.handleTestConnectionError}
                  isValid={isValid}
                  terminalAddress={values.address}
                  terminalName={values.name}
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="cirrus cr-flex cr-justify-end">
                <Group>
                  <Button
                    onClick={this.handleClose(onCancel, handleReset)}
                    disabled={isProcessing}
                    data-test="retail-payments-ccterminal-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    loading={isProcessing}
                    disabled={!isValid}
                    data-test="retail-payments-ccterminal-save"
                    fill
                  >
                    Save
                  </Button>
                </Group>
              </div>
            </ModalFooter>
          </Modal>
        )}
      />
    );
  }
}

TerminalModal.propTypes = {
  isOpen: PropTypes.bool,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  isProcessing: PropTypes.bool,
  terminalParameters: PropTypes.arrayOf(PropTypes.shape(PropTypes.any.object)),
  initialValues: PropTypes.shape(PropTypes.any.object),
  terminalGatewayID: PropTypes.string,
  ccTerminalErrors: PropTypes.arrayOf(PropTypes.shape(PropTypes.any.object)),
  edit: PropTypes.bool,
};

TerminalModal.defaultTypes = {
  isOpen: false,
  onCancel: () => {},
  onSuccess: () => {},
  isProcessing: false,
  terminalParameters: [],
  initialValues: {},
  terminalGatewayID: '',
  ccTerminalErrors: [],
  edit: false,
};

export default TerminalModal;
